<template>
  <div>
    <ChartCard>
      <ChartHeader
        label="Are all intended requisitions distributing the demographic survey?"
        tooltip-key="demo-survey-req-table"
      >
        <template #rightSide>
          <span class="requisition-count new-body-text">
            {{ formattedReqCount }} {{ reqFillRate.total === 1 ? 'requisition' : 'requisitions' }}
          </span>
        </template>
        <template #download>
          <FillRateCSVDownload
            class="ml-2"
          />
        </template>
      </ChartHeader>
      <div class="table-container">
        <ReqFillHeader
          :requisitions="requisitions"
          :current-sort="reqFillRateSort"
          @update:sort="handleSortUpdate"
        />
        <div v-if="!reqFillRateLoading && requisitions?.length" class="grid-body">
          <ReqFillRow
            v-for="({
              ats_requisition_label: atsRequisitionLabel,
              title,
              department,
              fill_rate: fillRate,
              status,
              requisition_ulid: requisitionUlid,
              ats_url: atsUrl,
            }) in requisitions"
            :key="requisitionUlid"
            :ats-requisition-label="atsRequisitionLabel"
            :title="title"
            :department="department"
            :fill-rate="fillRate"
            :status="status"
            :requisition-ulid="requisitionUlid"
            :ats-url="atsUrl"
          />
          <div class="w-100 d-flex align-center justify-center">
            <DpplNewButton
              v-if="canLoadMore && !reqFillRateLoadingMore"
              color="brandPrimary"
              class="my-2"
              size="small"
              @click="loadMore"
            >
              Load More
            </DpplNewButton>
            <v-progress-circular
              v-if="reqFillRateLoadingMore"
              indeterminate
              color="brandPrimary"
              size="30"
              class="my-2"
            />
          </div>
        </div>
        <div v-else-if="!reqFillRateLoading && !requisitions?.length" class="w-100 h-100 no-requisitions-container">
          <div class="bulleted-list-icon-container">
            <DpplSvg file-name="bulleted-list-primary" />
          </div>
          <span class="no-reqs-congrats new-h3">All of the requisitions in this filter set seem to be distributing the demographic survey.</span>
        </div>
        <div v-else class="w-100 h-100 d-flex align-center justify-center req-fill-rate-loading">
          <v-progress-circular
            indeterminate
            color="brandPrimary"
            size="40"
            class="my-2"
          />
        </div>
      </div>
    </ChartCard>
  </div>
</template>

<script setup>
import ChartCard from '@/components/insights/Chart/ChartCard.vue';
import ChartHeader from '@/components/insights/Chart/ChartHeader.vue';
import { useDemographicSurveyReport } from '@/stores/insights/demographicSurveyReport';
import { storeToRefs } from 'pinia';
import { computed } from 'vue';
import { numberWithCommas } from '@/helpers/formatters';
import ReqFillHeader from '@/components/insights/ProcessInsights/DemographicSurveyReport/tables/ReqFillHeader.vue';
import ReqFillRow from '@/components/insights/ProcessInsights/DemographicSurveyReport/tables/ReqFillRow.vue';
import { useRoute } from 'vue-router';
import DpplSvg from '@/components/datapeople/DpplSvg.vue';
import DpplNewButton from '@/components/datapeople/DpplNewButton.vue';
import FillRateCSVDownload from '@/components/insights/ProcessInsights/DemographicSurveyReport/tables/FillRateCSVDownload.vue';
import { useTracking } from '@/stores/tracking/tracking';

const {
  reqFillRate,
  reqFillRateLoadingMore,
  reqFillRateLoading,
  reqFillRateSort,
} = storeToRefs(useDemographicSurveyReport());
const {
  loadMoreFillRateRequisitions,
  fetchReqFillRate,
  updateReqFillRateSort,
} = useDemographicSurveyReport();

const route = useRoute();

const requisitions = computed(() => reqFillRate.value?.items || []);
const formattedReqCount = computed(() => numberWithCommas(reqFillRate.value?.total || 0));
const canLoadMore = computed(() => reqFillRate.value.pages > reqFillRate.value.page);

const loadMore = () => {
  loadMoreFillRateRequisitions({ routeQuery: route.query, page: reqFillRate.value.page + 1 });
};

const handleSortUpdate = ({ sort, sortDirection }) => {
  useTracking().track({
    name: 'Requisition Table Sorted in Demographic-survey-report page',
    params: {
      sort_column: sort,
    },
  });

  updateReqFillRateSort({ sort, sortDirection });
  fetchReqFillRate({ routeQuery: route.query });
};
</script>

<style lang="less" scoped>
.table-container {
  width: 100%;
  border-radius: 8px;
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  justify-content: flex-start;
}

.requisition-count {
  color: @text-light;
}

.grid-body {
  max-height: 540px;
  overflow-y: auto;
  width: 100%;
  min-width: 100%;
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  justify-content: flex-start;
}

.req-fill-rate-loading {
  min-height: 460px;
}

.no-requisitions-container {
  min-height: 460px;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
}

.bulleted-list-icon-container {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 72px;
  height: 72px;
  border-radius: 50%;
  background: fade(@bg-brand-default, 10%);
}

.no-reqs-congrats {
  max-width: 248px;
  color: @text-dark;
  text-align: center;
  margin-top: 4px;
}
</style>
